import React from 'react';
import CodeBlock from '@/components/CodeBlock/CodeBlock';
import { ProTip } from '../../components/LessonUI';

const PythonPathlib = () => {
    return (
        <div className="tutorial-content-module">
            <div className="premium-header">
                <h1>Pathlib: Modern File Systems</h1>
                <p className="subtitle">Stop manipulating string paths. Start treating the File System as an Object-Oriented structure.</p>
            </div>

            <div className="content-section">
                <h2>1. The Big Idea (ELI5)</h2>
                <ProTip><p>
                        Imagine you want to tell a robot how to get to the kitchen.
                    </p>
                    <ul>
                        <li><strong>Old Way (Strings):</strong> You give a written note: "Go forward 10 steps, turn left, forward 5." If the robot is in a different house (OS), it hits a wall.</li>
                        <li><strong>New Way (Pathlib):</strong> You give the robot a <strong>GPS Coordinate</strong>. The robot (Path Object) is smart. It knows exactly how to navigate the current house, whether the doors open left or right (`/` vs `\`), and if the destination exists.</li>
                    </ul></ProTip>
            </div>

            <div className="content-section">
                <h2>2. The Syntax: Operator Overloading</h2>
                <p>
                    In the old days (`os.path`), we had to nest functions like `os.path.join(a, b)`.
                    Pathlib reuses the division operator `/` to join paths naturally.
                </p>
                <CodeBlock language="python" code={`from pathlib import Path

# Create a Path object for the current folder
folder = Path("data_science_project")

# Join paths using /
# Windows: data_science_project\\raw_data\\images
# Linux:   data_science_project/raw_data/images
full_path = folder / "raw_data" / "images" 

# It automatically handles the OS separator!
print(full_path)`} />
            </div>

            <div className="content-section">
                <h2>3. Essential Operations</h2>
                <p>
                    Pathlib consolidates operations that used to be scattered across `os`, `os.path`, and `glob`.
                </p>
                <CodeBlock language="python" code={`p = Path("report.txt")

# 1. Existence Check
if p.exists():
    print("Found it!")

# 2. Reading text (One-liner!)
content = p.read_text(encoding="utf-8")

# 3. file parts
print(p.name)   # report.txt
print(p.stem)   # report (No extension)
print(p.suffix) # .txt

# 4. Creating Directories (Recursive)
# Creates /users/rohit/new/deep/folder even if parents don't exist
Path("new/deep/folder").mkdir(parents=True, exist_ok=True)`} />
            </div>

            <div className="content-section">
                <h2>4. Deep Dive: Recursive Globbing</h2>
                <p>
                    Finding files is where Pathlib shines.
                    `glob()` matches files in the current folder. `rglob()` matches files in <strong>all subfolders</strong> (Recursive).
                </p>
                <CodeBlock language="python" code={`# Find all Python files anywhere in the project
root = Path(".")
py_files = root.rglob("*.py") # Returns a Generator!

for file_path in py_files:
    print(f"Checking {file_path.name}...")
    
    # Calculate file size
    size_kb = file_path.stat().st_size / 1024
    if size_kb &gt; 100:
        print(f" Large File Alert: {size_kb:.1f}KB")`} />
                <ProTip><p><strong>Efficiency:</strong> `rglob` returns a generator, meaning it doesn't build a list of 1,000,000 files in RAM. It finds them one by one as you loop.</p></ProTip>
            </div>

            <div className="content-section">
                <h2>5. System Agnosticism</h2>
                <p>
                    One of the biggest bugs in Python scripts is hardcoded paths like `C:\\Users\\Bob`.
                    This crashes instantly on Linux Servers.
                </p>
                <p>
                    Pathlib fixes this with special properties:
                </p>
                <CodeBlock language="python" code={`# Get the User's Home Directory (Works on Mac/Lin/Win)
home = Path.home()
# e.g., /home/ubuntu or C:\\Users\\Administrator

# Get the Current Working Directory
cwd = Path.cwd()

# Make a path relative to the script location
# __file__ is the current script's path
script_location = Path(__file__).parent
config_file = script_location / "config.ini"`} />
            </div>

            <div className="content-section">
                <h2>6. Comparison: `os` vs `pathlib`</h2>
                <table className="premium-table">
                    <thead>
                        <tr>
                            <th>Task</th>
                            <th>Old (`os`)</th>
                            <th>New (`pathlib`)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Join Paths</td>
                            <td>`os.path.join(a, b)`</td>
                            <td>`a / b`</td>
                        </tr>
                        <tr>
                            <td>Make Dir</td>
                            <td>`os.makedirs(p)`</td>
                            <td>`p.mkdir(parents=True)`</td>
                        </tr>
                        <tr>
                            <td>File Ext</td>
                            <td>`os.path.splitext(p)[1]`</td>
                            <td>`p.suffix`</td>
                        </tr>
                    </tbody>
                </table>
            </div>

        </div>
    );
};

export default PythonPathlib;


